import { useRef, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail, fileUrl } from "@/lib/api";
import { fmtDate } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { CheckCircle, FileArrowUp, SealCheck, Warning } from "@phosphor-icons/react";

export default function MontajFinalizare({ project, reload }) {
  const p = project;
  const pvRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [url, setUrl] = useState(p.pv_external_url || "");

  const patch = async (body, msg) => {
    setBusy(true);
    try {
      await api.put(`/projects/${p.id}/montaj-finalizare`, body);
      if (msg) toast.success(msg);
      reload && reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const onPv = async (e) => {    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/projects/${p.id}/pv-montaj`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("PV de finalizare montaj încărcat");
      reload && reload();
    } catch (e2) { toast.error(formatApiErrorDetail(e2.response?.data?.detail)); }
    finally { setBusy(false); if (pvRef.current) pvRef.current.value = ""; }
  };

  const fetchPv = async () => {
    if (!url) { toast.error("Adaugă întâi linkul PV"); return; }
    setBusy(true);
    try {
      await api.post(`/projects/${p.id}/pv-from-url`, { url });
      toast.success("PV preluat și atașat din highberg-pv.online");
      reload && reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const Row = ({ label, done, at, children }) => (
    <div className="flex items-center justify-between gap-3 rounded-lg border border-border px-4 py-3">
      <div>
        <div className="flex items-center gap-2 text-sm font-medium">
          {done ? <CheckCircle size={16} weight="fill" className="text-emerald-400" /> : <Warning size={16} className="text-amber-400" />}
          {label}
        </div>
        {done && at && <div className="text-[11px] text-muted-foreground">{fmtDate(at)}</div>}
      </div>
      {children}
    </div>
  );

  return (
    <div className="space-y-4" data-testid="montaj-finalizare-panel">
      <div className="rounded-xl border border-border bg-card p-6">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="flex items-center gap-2 font-head text-lg font-bold"><SealCheck size={20} /> Finalizare montaj</h3>
          {p.montaj_finalizat
            ? <span className="rounded-full bg-emerald-500/15 px-3 py-1 text-xs font-bold text-emerald-400" data-testid="montaj-final-badge">MONTAJ FINALIZAT</span>
            : <span className="rounded-full bg-white/5 px-3 py-1 text-xs text-muted-foreground">În desfășurare</span>}
        </div>

        <div className="space-y-3">
          <Row label="PV de finalizare montaj (obligatoriu)" done={p.pv_montaj_uploaded} at={p.pv_montaj_at}>
            <div className="flex items-center gap-2">
              {p.pv_montaj_uploaded && p.pv_montaj_path && (
                <a href={fileUrl(p.pv_montaj_path)} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline">Vezi PV</a>
              )}
              <input ref={pvRef} type="file" className="hidden" onChange={onPv} data-testid="pv-file-input" />
              <Button size="sm" variant="outline" onClick={() => pvRef.current?.click()} disabled={busy} data-testid="pv-upload-btn">
                <FileArrowUp size={15} className="mr-1.5" /> {p.pv_montaj_uploaded ? "Înlocuiește" : "Încarcă PV"}
              </Button>
            </div>
          </Row>

          <Row label="Carnet de service predat clientului" done={p.carnet_service_predat} at={p.carnet_service_at}>
            <Switch checked={!!p.carnet_service_predat} disabled={busy}
              onCheckedChange={(v) => patch({ carnet_service_predat: v }, v ? "Carnet service marcat predat" : "Anulat")}
              data-testid="carnet-service-switch" />
          </Row>

          <Row label="Documentație completă predată" done={p.documentatie_predata} at={p.documentatie_at}>
            <Switch checked={!!p.documentatie_predata} disabled={busy}
              onCheckedChange={(v) => patch({ documentatie_predata: v }, v ? "Documentație marcată predată" : "Anulat")}
              data-testid="documentatie-switch" />
          </Row>
        </div>

        <div className="mt-4 grid gap-1.5">
          <Label className="text-xs">Link PV extern (highberg-pv.online)</Label>
          <div className="flex flex-wrap gap-2">
            <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://highberg-pv.online/admin/…" className="min-w-[200px] flex-1" data-testid="pv-external-url" />
            <Button variant="outline" size="sm" onClick={() => patch({ pv_external_url: url }, "Link PV salvat")} disabled={busy}>Salvează link</Button>
            <Button size="sm" onClick={fetchPv} disabled={busy || !url} data-testid="pv-from-url-btn">Preia PV din link</Button>
          </div>
          <p className="text-[11px] text-muted-foreground">Pregătit pentru interconectarea cu platforma de PV-uri highberg-pv.online.</p>
        </div>

        <div className="mt-5 flex justify-end">
          {p.montaj_finalizat ? (
            <Button variant="outline" onClick={() => patch({ montaj_finalizat: false }, "Finalizare anulată")} disabled={busy} data-testid="montaj-unfinalize-btn">
              Redeschide montajul
            </Button>
          ) : (
            <Button onClick={() => patch({ montaj_finalizat: true }, "Montaj finalizat")} disabled={busy || !p.pv_montaj_uploaded} data-testid="montaj-finalize-btn">
              <SealCheck size={16} className="mr-1.5" /> Finalizează montajul
            </Button>
          )}
        </div>
        {!p.pv_montaj_uploaded && <p className="mt-2 text-right text-[11px] text-amber-400">Încarcă întâi PV-ul de finalizare montaj.</p>}
      </div>
    </div>
  );
}
